import { useMemo, useRef } from "react";
import clsx from "clsx";
import { useDroppable } from "@dnd-kit/core";
import type { POI, Tag, DayView } from "@poi-planner/shared";
import {
  blockGeometry,
  columnHeightPx,
  instantToY,
  wallTimeToY,
  yToZonedTime,
  MIN_SPAN_MINUTES,
  SNAP_MINUTES_POINTER,
  SNAP_MINUTES_TOUCH,
  minBlockHeightPx,
} from "../../utils/timeGrid";
import { computeOverlapLayout } from "../../utils/calendarLayout";
import { computeTravelSegments } from "../../utils/travel";
import { tripTravelModePreferences } from "../../utils/geo";
import { blockEndTime, blockStartTime } from "../../utils/dayViews";
import {
  addMinutes,
  epochMs,
  zonedDate,
  zonedTimeOfDay,
} from "../../utils/zonedTime";
import { dndIds } from "../../utils/dndIds";
import {
  hasUsableCoordinates,
  resolveNoteMapPoi,
} from "../../utils/dayMapPois";
import { useCalendarPxPerMinute, useUIStore } from "../../store/uiStore";
import { useCalendarDndStore } from "../../store/calendarDndStore";
import { useTripStore } from "../../store/tripStore";
import { useResolvedTravel } from "../../hooks/useResolvedTravel";
import { useNow } from "../../hooks/useNow";
import {
  dayContainsNow,
  isAllDayNotePast,
  isBlockPast,
  nowInZone,
} from "../../utils/nowTime";
import { CalendarBlock } from "./CalendarBlock";
import { NoteBlock } from "./NoteBlock";
import { NowLine } from "./NowLine";
import { TravelConnector } from "./TravelConnector";
import type { DayAccommodationContext } from "./types";
import type { CalendarDayDropData } from "./dragTypes";

interface CalendarDayColumnProps {
  dayView: DayView;
  accommodation: DayAccommodationContext;
  pois: POI[];
  tags: Tag[];
  isReadOnly: boolean;
  onShowOnMap?: (poiId: string) => void;
}

interface CheckMarker {
  kind: "checkin" | "checkout";
  label: string;
  y: number;
}

interface CalendarDayDropSurfaceProps {
  date: string;
  zone: string;
  height: number;
  background: string;
  children: React.ReactNode;
  onPointerDown: React.PointerEventHandler<HTMLDivElement>;
  onPointerMove: React.PointerEventHandler<HTMLDivElement>;
  onPointerUp: React.PointerEventHandler<HTMLDivElement>;
  onPointerCancel: React.PointerEventHandler<HTMLDivElement>;
}

/** Keeps dnd-kit context updates from rerendering the full day column. */
function CalendarDayDropSurface({
  date,
  zone,
  height,
  background,
  children,
  onPointerDown,
  onPointerMove,
  onPointerUp,
  onPointerCancel,
}: CalendarDayDropSurfaceProps) {
  const { setNodeRef, isOver } = useDroppable({
    id: dndIds.calendarDay(date),
    data: { type: "calendar-day", date, zone } satisfies CalendarDayDropData,
  });

  return (
    <div
      ref={setNodeRef}
      className={clsx(
        "relative isolate overflow-hidden rounded-2xl border transition-shadow max-md:snap-start",
        isOver
          ? "border-info shadow-[0_0_0_1px_var(--color-info)]"
          : "border-border",
      )}
      style={{ height, background }}
      data-calendar-date={date}
      data-calendar-zone={zone}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
      onPointerCancel={onPointerCancel}
    >
      {children}
    </div>
  );
}

/** Sweep gesture state for note drag-create */
interface Sweep {
  originTime: string;
  currentTime: string;
  originClientX: number;
  originClientY: number;
  moved: boolean;
}

const SWEEP_INTENT_PX = 6;
const LONG_PRESS_MS = 400;
const LONG_PRESS_TOLERANCE_PX = 10;

/**
 * One day's 24h canvas: accommodation tint (with swap-day gradient),
 * hour/half-hour gridlines, check-in/out markers, blocks in Apple-cascade
 * layout, and travel pins in the left gutter between consecutive blocks.
 *
 * Editing: the column is a dnd-kit droppable (renders the live drop preview
 * while a drag hovers it) and hosts note drag-create — a raw-pointer sweep
 * on empty canvas (mouse/pen), or a long-press on touch.
 */
export function CalendarDayColumn({
  dayView,
  accommodation,
  pois,
  tags,
  isReadOnly,
  onShowOnMap,
}: CalendarDayColumnProps) {
  const { date, zone, blocks, allDayBlocks } = dayView;
  const pxPerMinute = useCalendarPxPerMinute();
  const height = columnHeightPx(date, zone, pxPerMinute);

  const openNoteEditor = useUIStore((s) => s.openNoteEditor);
  const now = useNow();
  const nowTop = dayContainsNow(date, zone, now)
    ? instantToY(nowInZone(now, zone), date, zone, pxPerMinute)
    : null;
  const pastBlockIds = useMemo(
    () =>
      new Set(
        blocks.filter((block) => isBlockPast(block, now)).map((b) => b.id),
      ),
    [blocks, now],
  );
  const dropPreview = useCalendarDndStore((s) =>
    s.preview?.date === date ? s.preview : null,
  );
  const creationPreview = useCalendarDndStore((s) => s.creationPreview);
  const resizePreview = useCalendarDndStore((s) => s.resizePreview);
  const dateRangePreview = useCalendarDndStore((s) => s.dateRangePreview);
  const setCreationPreview = useCalendarDndStore((s) => s.setCreationPreview);

  const sweepRef = useRef<Sweep | null>(null);
  const longPress = useRef<{
    timer: ReturnType<typeof setTimeout>;
    originY: number;
  } | null>(null);

  const poiMap = useMemo(
    () => new Map(pois.map((poi) => [poi.id, poi])),
    [pois],
  );
  const tagMap = useMemo(
    () => new Map(tags.map((tag) => [tag.id, tag])),
    [tags],
  );

  const layouts = useMemo(
    () =>
      computeOverlapLayout(
        blocks.map((block) => ({
          id: block.id,
          startTime: blockStartTime(block),
          endTime: blockEndTime(block),
        })),
      ),
    [blocks],
  );

  // Computed once per [blocks, date, zone] change rather than per block on
  // every render — the column re-renders on each drop/resize/creation
  // preview tick during a drag, and blockGeometry parses each endpoint
  // through Temporal.
  const geometries = useMemo(() => {
    const result = new Map<string, ReturnType<typeof blockGeometry>>();
    for (const block of blocks) {
      result.set(
        block.id,
        blockGeometry(
          blockStartTime(block),
          blockEndTime(block),
          date,
          zone,
          {
            fromPreviousDay: !block.segment.isStart,
            intoNextDay: !block.segment.isEnd,
          },
          pxPerMinute,
        ),
      );
    }
    return result;
  }, [blocks, date, zone, pxPerMinute]);

  const travelModes = useTripStore((s) => s.schedule.travelModes);
  const defaultTravelMode = useTripStore((s) => s.trip?.defaultTravelMode);
  const walkThresholdKm = useTripStore((s) => s.trip?.walkThresholdKm);
  const rawTravelSegments = useMemo(
    () =>
      computeTravelSegments(
        dayView,
        pois,
        travelModes,
        tripTravelModePreferences({ defaultTravelMode, walkThresholdKm }),
      ),
    [dayView, pois, travelModes, defaultTravelMode, walkThresholdKm],
  );
  const travelSegments = useResolvedTravel(rawTravelSegments);

  // Check-in/check-out dashed markers from the day's accommodation configs
  const markers = useMemo((): CheckMarker[] => {
    const result: CheckMarker[] = [];
    const { sleepPoi, sleepConfig, wakePoi, wakeConfig } = accommodation;
    if (wakePoi && wakePoi.id !== sleepPoi?.id) {
      const time = wakeConfig?.checkOutTime ?? "11:00";
      result.push({
        kind: "checkout",
        label: `Check-out ${time}`,
        y: wallTimeToY(time, date, zone, pxPerMinute),
      });
    }
    if (sleepPoi && sleepPoi.id !== wakePoi?.id) {
      const time = sleepConfig?.checkInTime ?? "15:00";
      result.push({
        kind: "checkin",
        label: `Check-in ${time}`,
        y: wallTimeToY(time, date, zone, pxPerMinute),
      });
    }
    return result;
  }, [accommodation, date, zone, pxPerMinute]);

  const { sleepPalette, wakePalette, isSwap } = accommodation;
  const tint =
    isSwap && wakePalette && sleepPalette
      ? `linear-gradient(to bottom, ${wakePalette.bg} 0%, ${wakePalette.bg} 34%, ${sleepPalette.bg} 62%, ${sleepPalette.bg} 100%)`
      : (sleepPalette?.bg ?? "var(--color-surface)");

  // Travel pin span: from the previous block's rendered bottom to the next
  // block's top, so the rail stays in the gap even when a short block is
  // drawn taller than its span. Pins outside the column are dropped.
  const travelGap = (
    fromId: string,
    toId: string,
  ): { top: number; bottom: number } | null => {
    const from = geometries.get(fromId);
    const to = geometries.get(toId);
    if (!from || !to) return null;
    const top = from.top + from.height;
    const bottom = Math.max(to.top, top);
    const mid = (top + bottom) / 2;
    return mid >= 0 && mid <= height ? { top, bottom } : null;
  };

  // ── Note drag-create (raw pointer events; never fights dnd-kit — blocks
  // are ignored via the closest() guard and dnd-kit only listens on them) ──

  const isEmptyCanvasEvent = (e: React.PointerEvent) =>
    !(e.target as Element).closest(
      "[data-scheduled-poi-id], [data-note-id], button",
    );

  const localY = (e: React.PointerEvent) => {
    const rect = e.currentTarget.getBoundingClientRect();
    return e.clientY - rect.top;
  };

  const timeAtPointer = (e: React.PointerEvent, snap: number) => {
    const hit = document.elementFromPoint(e.clientX, e.clientY);
    const column = hit?.closest<HTMLElement>("[data-calendar-date]");
    const targetDate = column?.dataset.calendarDate ?? date;
    const targetZone = column?.dataset.calendarZone ?? zone;
    const rect =
      column?.getBoundingClientRect() ??
      e.currentTarget.getBoundingClientRect();
    return yToZonedTime(
      e.clientY - rect.top,
      targetDate,
      targetZone,
      snap,
      pxPerMinute,
    );
  };

  const openCreateEditor = (first: string, second: string) => {
    const [startTime, rawEndTime] =
      epochMs(first) <= epochMs(second) ? [first, second] : [second, first];
    let endTime = rawEndTime;
    if (epochMs(endTime) - epochMs(startTime) < MIN_SPAN_MINUTES * 60_000) {
      endTime = addMinutes(startTime, MIN_SPAN_MINUTES);
    }
    openNoteEditor("create", undefined, {
      type: "timed",
      startTime,
      endTime,
    });
  };

  const handlePointerDown = (e: React.PointerEvent) => {
    if (isReadOnly || !isEmptyCanvasEvent(e)) return;

    if (e.pointerType === "touch") {
      // Long-press opens the editor with a default 1h span — sweeping would
      // fight one-finger scrolling on the PWA
      const originY = localY(e);
      longPress.current = {
        originY,
        timer: setTimeout(() => {
          longPress.current = null;
          const startTime = yToZonedTime(
            originY,
            date,
            zone,
            SNAP_MINUTES_TOUCH,
            pxPerMinute,
          );
          openNoteEditor("create", undefined, {
            type: "timed",
            startTime,
            endTime: addMinutes(startTime, 60),
          });
        }, LONG_PRESS_MS),
      };
      return;
    }

    if (e.button !== 0) return;
    const originTime = timeAtPointer(e, SNAP_MINUTES_POINTER);
    const origin = {
      originTime,
      currentTime: originTime,
      originClientX: e.clientX,
      originClientY: e.clientY,
      moved: false,
    };
    sweepRef.current = origin;
    e.currentTarget.setPointerCapture(e.pointerId);
  };

  const handlePointerMove = (e: React.PointerEvent) => {
    if (longPress.current) {
      const y = localY(e);
      if (Math.abs(y - longPress.current.originY) > LONG_PRESS_TOLERANCE_PX) {
        clearTimeout(longPress.current.timer);
        longPress.current = null;
      }
      return;
    }

    const current = sweepRef.current;
    if (!current) return;
    const currentTime = timeAtPointer(e, SNAP_MINUTES_POINTER);
    const next: Sweep = {
      ...current,
      currentTime,
      moved:
        current.moved ||
        Math.hypot(
          e.clientX - current.originClientX,
          e.clientY - current.originClientY,
        ) > SWEEP_INTENT_PX,
    };
    sweepRef.current = next;
    const interval =
      epochMs(next.originTime) <= epochMs(next.currentTime)
        ? { startTime: next.originTime, endTime: next.currentTime }
        : { startTime: next.currentTime, endTime: next.originTime };
    setCreationPreview(interval);
  };

  const handlePointerUp = (e: React.PointerEvent) => {
    if (longPress.current) {
      clearTimeout(longPress.current.timer);
      longPress.current = null;
    }

    const current = sweepRef.current;
    sweepRef.current = null;
    setCreationPreview(null);
    if (!current || !current.moved) return;

    const snap =
      e.pointerType === "touch" ? SNAP_MINUTES_TOUCH : SNAP_MINUTES_POINTER;
    openCreateEditor(current.originTime, timeAtPointer(e, snap));
  };

  const handlePointerCancel = () => {
    if (longPress.current) {
      clearTimeout(longPress.current.timer);
      longPress.current = null;
    }
    sweepRef.current = null;
    setCreationPreview(null);
  };

  // Sweep phantom geometry (unsnapped while sweeping; snap happens on release)
  const creationVisible = creationPreview
    ? instantToY(creationPreview.startTime, date, zone, pxPerMinute) < height &&
      instantToY(creationPreview.endTime, date, zone, pxPerMinute) > 0
    : false;
  const sweepRect = creationVisible
    ? blockGeometry(
        creationPreview!.startTime,
        creationPreview!.endTime,
        date,
        zone,
        undefined,
        pxPerMinute,
      )
    : null;
  const resizeVisible = resizePreview
    ? instantToY(resizePreview.startTime, date, zone, pxPerMinute) < height &&
      instantToY(resizePreview.endTime, date, zone, pxPerMinute) > 0
    : false;
  const resizeRect = resizeVisible
    ? blockGeometry(
        resizePreview!.startTime,
        resizePreview!.endTime,
        date,
        zone,
        undefined,
        pxPerMinute,
      )
    : null;

  const previewTop = dropPreview
    ? instantToY(dropPreview.startTime, date, zone, pxPerMinute)
    : null;
  const previewHeight = dropPreview
    ? Math.max(
        instantToY(dropPreview.endTime, date, zone, pxPerMinute) -
          (previewTop ?? 0),
        minBlockHeightPx(pxPerMinute),
      )
    : null;

  return (
    <CalendarDayDropSurface
      date={date}
      zone={zone}
      height={height}
      background={tint}
      onPointerDown={handlePointerDown}
      onPointerMove={handlePointerMove}
      onPointerUp={handlePointerUp}
      onPointerCancel={handlePointerCancel}
    >
      <div
        className="calendar-gridlines pointer-events-none absolute inset-0"
        style={
          {
            "--calendar-hour-height": `${pxPerMinute * 60}px`,
          } as React.CSSProperties
        }
      />

      {allDayBlocks.length > 0 && (
        <div
          className={clsx(
            "calendar-all-day-backdrop pointer-events-none absolute inset-0 z-[1] flex items-center justify-center px-3 text-center text-calendar-all-day-text",
            allDayBlocks.every(({ note }) =>
              isAllDayNotePast(note.allDayEndDate, zone, now),
            ) && "calendar-past",
          )}
          aria-hidden
          data-testid="all-day-backdrop"
        >
          <span className="rounded-md bg-surface/75 px-2.5 py-1.5 text-xs font-bold shadow-sm backdrop-blur-[1px]">
            {allDayBlocks[0]?.note.name}
            <span className="block text-2xs font-extrabold uppercase tracking-wider opacity-70">
              All day
              {allDayBlocks.length > 1 && ` · +${allDayBlocks.length - 1} more`}
            </span>
          </span>
        </div>
      )}

      {dateRangePreview &&
        date >= dateRangePreview.startDate &&
        date <= dateRangePreview.endDate && (
          <div
            className={clsx(
              "pointer-events-none absolute inset-0 z-[2] ring-2 ring-inset",
              dateRangePreview.invalid
                ? "bg-error-bg ring-error"
                : "bg-calendar-all-day-wash ring-calendar-all-day-border",
            )}
            aria-hidden
            data-testid="date-range-preview"
          />
        )}

      {markers.map((marker) => (
        <div
          key={marker.kind}
          className="pointer-events-none absolute inset-x-0 z-[1] flex -translate-y-1/2 items-center gap-1.5 px-2 text-info"
          style={{ top: marker.y }}
        >
          <span className="whitespace-nowrap rounded bg-blue-100 px-1.5 py-px text-2xs font-extrabold uppercase tracking-wider text-blue-800">
            {marker.label}
          </span>
          <span className="flex-1 border-t-2 border-dashed border-current opacity-50" />
        </div>
      ))}

      {blocks.map((block) => {
        const layout = layouts.get(block.id);
        const geometry = geometries.get(block.id);
        if (!layout || !geometry) return null;

        if (block.type === "poi") {
          const poi = poiMap.get(block.scheduledPOI.poiId);
          if (!poi) return null;
          return (
            <CalendarBlock
              key={block.id}
              scheduledPOI={block.scheduledPOI}
              poi={poi}
              primaryTag={
                poi.primaryTagId ? tagMap.get(poi.primaryTagId) : undefined
              }
              geometry={geometry}
              layout={layout}
              date={date}
              zone={zone}
              accentColor={sleepPalette?.text ?? "var(--color-neutral)"}
              isReadOnly={isReadOnly}
              isPast={pastBlockIds.has(block.id)}
              segment={block.segment}
              onShowOnMap={
                onShowOnMap && hasUsableCoordinates(poi)
                  ? () => onShowOnMap(poi.id)
                  : undefined
              }
            />
          );
        }

        const startPoi = block.note.startPoiId
          ? poiMap.get(block.note.startPoiId)
          : undefined;
        const endPoi = block.note.endPoiId
          ? poiMap.get(block.note.endPoiId)
          : undefined;
        const noteMapPoi = resolveNoteMapPoi(startPoi, endPoi);

        return (
          <NoteBlock
            key={block.id}
            note={block.note}
            tags={block.note.tagIds
              .map((tagId) => tagMap.get(tagId))
              .filter((tag): tag is Tag => !!tag)}
            startPoi={startPoi}
            endPoi={endPoi}
            geometry={geometry}
            layout={layout}
            date={date}
            zone={zone}
            isReadOnly={isReadOnly}
            isPast={pastBlockIds.has(block.id)}
            segment={block.segment}
            onShowOnMap={
              onShowOnMap && noteMapPoi
                ? () => onShowOnMap(noteMapPoi.id)
                : undefined
            }
          />
        );
      })}

      {travelSegments.map((segment) => {
        const gap = travelGap(segment.fromBlockId, segment.toBlockId);
        if (!gap) return null;
        return (
          <TravelConnector
            key={segment.segmentKey}
            segment={segment}
            gapTop={gap.top}
            gapBottom={gap.bottom}
            isReadOnly={isReadOnly}
            isPast={pastBlockIds.has(segment.toBlockId)}
          />
        );
      })}

      {nowTop !== null && <NowLine top={nowTop} />}

      {/* Live drop preview: phantom sized to the dragged span + snapped time readout */}
      {dropPreview && previewTop !== null && (
        <>
          <div
            className="pointer-events-none absolute left-1 right-1 z-30 h-0.5 rounded bg-info"
            style={{ top: previewTop }}
          >
            <span className="absolute left-1 top-1/2 -translate-y-1/2 rounded-md bg-info px-1.5 py-0.5 text-2xs font-extrabold text-white tabular-nums">
              {dropPreview.timeLabel}
            </span>
          </div>
          <div
            className="pointer-events-none absolute left-2 right-2 z-20 flex items-start rounded-lg border-2 border-dashed border-info bg-info/10 px-2 py-1"
            style={{ top: previewTop, height: previewHeight ?? 0 }}
          >
            <span className="truncate text-xs font-bold text-blue-700 opacity-85">
              {dropPreview.label}
            </span>
          </div>
        </>
      )}

      {/* Drag-create sweep phantom */}
      {sweepRect && (
        <div
          className="pointer-events-none absolute left-2 right-2 z-20 flex items-start rounded-lg border-2 border-dashed border-info bg-info/10 px-2 py-1"
          style={{ top: sweepRect.top, height: Math.max(sweepRect.height, 4) }}
        >
          <span className="text-2xs font-bold text-blue-700 tabular-nums">
            {creationPreview && zonedDate(creationPreview.startTime) !== date
              ? `${zonedDate(creationPreview.startTime)} `
              : ""}
            {creationPreview && zonedTimeOfDay(creationPreview.startTime)}
            {" – "}
            {creationPreview && zonedDate(creationPreview.endTime) !== date
              ? `${zonedDate(creationPreview.endTime)} `
              : ""}
            {creationPreview && zonedTimeOfDay(creationPreview.endTime)}
          </span>
        </div>
      )}

      {resizeRect && (
        <div
          className="pointer-events-none absolute left-1 right-1 z-40 rounded-lg border-2 border-dashed border-warning bg-amber-100/35"
          style={{ top: resizeRect.top, height: resizeRect.height }}
          aria-hidden
        />
      )}
    </CalendarDayDropSurface>
  );
}
